import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { authApi, playerProfileApi, socialApi } from '../services/api';
import { parseShadowOrgSlug } from '../lib/tenantPath';

function normTournamentStatusForApi(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

interface PlayerData {
  userId: string;
  username: string;
  name: string;
  uscfId?: string;
  fideId?: string;
  rating?: number;
  state?: string;
  email?: string;
  memberId?: string;
  phone?: string;
  notificationsEnabled?: boolean;
  lichessUsername?: string;
  chesscomUsername?: string;
  hideLichessPublic?: boolean;
  hideChesscomPublic?: boolean;
  socialProfileId?: string;
  avatarUrl?: string;
  /** True once the player has finished first-login onboarding (entered a US Chess ID or opted out). */
  profileComplete?: boolean;
  /** Synthetic hashed pairing identifier assigned when the player has no US Chess ID. */
  pairingUsername?: string;
}

interface RegisteredTournament {
  tournamentId: string;
  tournamentName: string;
  /** Roster/link id when known; pairing-only discoveries may omit */
  playerId?: string;
  section?: string;
  status: string;
  currentRound?: number;
  totalRounds?: number;
  startDate?: string;
  /** True for league parents (`tournaments.is_league`), which host no games of their own. */
  isLeague?: boolean;
  playerScore?: number;
  playerRank?: number;
  playerStatus?: string;
  playerEmail?: string;
  allowWithdraw?: boolean;
  allowReentry?: boolean;
  waitlistPosition?: number;
  waitlistRegistrationId?: string;
  waitlistOfferExpiresAt?: string;
}

interface PlayerAuthContextType {
  player: PlayerData | null;
  authToken: string | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
  registeredTournaments: RegisteredTournament[];
  needsProfileCompletion: boolean;
  accountLogin: (identifier: string, password: string) => Promise<boolean>;
  accountSignup: (payload: { username: string; email: string; password: string }) => Promise<boolean>;
  loginWithToken: (token: string, user: any) => Promise<boolean>;
  completeProfile: (payload: { uscfId?: string; noUscfId?: boolean; phone?: string; firstName?: string; lastName?: string; fideId?: string }) => Promise<boolean>;
  startGoogleLogin: () => void;
  startLichessLogin: () => void;
  startChesscomLogin: () => Promise<void>;
  logout: () => void;
  updatePlayerInfo: (data: Partial<PlayerData>) => void;
  /**
   * Put a US Chess ID on the account from inside a form — the registration
   * or club form, when the player finds their record there. Sends only the
   * ID (the server keeps everything else) and, unlike completeProfile, does
   * not raise the app-wide loading state, which would swap the form for a
   * splash mid-entry.
   */
  saveUscfId: (uscfId: string) => Promise<{ success: boolean; error?: string }>;
  updateLinkedAccounts: (data: { lichessUsername?: string; chesscomUsername?: string }) => Promise<boolean>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  changeEmail: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  refreshTournaments: () => Promise<void>;
}

const PlayerAuthContext = createContext<PlayerAuthContextType | undefined>(undefined);

const PLAYER_STORAGE_KEY = 'paircraft_player_auth';

interface StoredPlayerAuth {
  token: string;
  player: PlayerData;
}

export const PlayerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [player, setPlayer] = useState<PlayerData | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registeredTournaments, setRegisteredTournaments] = useState<RegisteredTournament[]>([]);

  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const stored = localStorage.getItem(PLAYER_STORAGE_KEY);
        if (stored) {
          const parsed: StoredPlayerAuth = JSON.parse(stored);
          if (parsed?.token && parsed?.player) {
            await hydrateFromToken(parsed.token, parsed.player);
          }
        }
      } catch (err) {
        console.error('Error loading stored player:', err);
        localStorage.removeItem(PLAYER_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistAuth = (token: string, playerData: PlayerData) => {
    const payload: StoredPlayerAuth = { token, player: playerData };
    try {
      localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // Avatar data URLs can exceed localStorage quota; session still works via in-memory state.
      console.warn('Player auth cache too large for localStorage, omitting avatar:', err);
      try {
        const { avatarUrl: _omit, ...rest } = playerData;
        localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify({ token, player: rest }));
      } catch {
        /* ignore */
      }
    }
  };

  /** Loads tournaments whenever we have a player session token (`me` resolves US Chess ID on server). */
  const fetchRegisteredTournaments = async (uscfHint?: string, bearerOverride?: string | null) => {
    const bearer = bearerOverride ?? authToken;
    if (!bearer) {
      setRegisteredTournaments([]);
      return;
    }

    const segment = (uscfHint && String(uscfHint).trim()) || 'me';

    try {
      const response = await playerProfileApi.getTournaments(segment, { bearerToken: bearer });

      if (response.data?.success && response.data?.tournaments) {
        const mappedTournaments = response.data.tournaments.map((t: any) => ({
          tournamentId: t.tournament_id || t.id,
          tournamentName: t.tournament_name || t.name,
          playerId: t.player_id != null ? String(t.player_id) : undefined,
          section: t.section,
          status: normTournamentStatusForApi(t.tournament_status ?? t.status),
          currentRound: t.current_round,
          totalRounds: t.total_rounds || t.rounds,
          startDate: t.start_date,
          isLeague: Boolean(Number(t.is_league ?? 0)),
          playerScore: t.player_score || t.score,
          playerRank: t.player_rank || t.rank,
          playerStatus: t.player_status,
          playerEmail: t.player_email,
          allowWithdraw: Boolean(t.allow_withdraw),
          allowReentry: Boolean(t.allow_reentry),
          waitlistPosition:
            t.waitlist_position != null && t.waitlist_position !== ''
              ? Number(t.waitlist_position)
              : undefined,
          waitlistRegistrationId: t.waitlist_registration_id || undefined,
          waitlistOfferExpiresAt: t.waitlist_offer_expires_at || undefined,
        }));
        
        // Deduplicate by tournamentId
        const seen = new Set<string>();
        const uniqueTournaments = mappedTournaments.filter((t: any) => {
          if (seen.has(t.tournamentId)) {
            return false;
          }
          seen.add(t.tournamentId);
          return true;
        });
        
        setRegisteredTournaments(uniqueTournaments);
      } else {
        setRegisteredTournaments([]);
      }
    } catch (err) {
      console.error('Error fetching registered tournaments:', err);
      setRegisteredTournaments([]);
    }
  };

  const hydrateFromToken = async (token: string, fallbackPlayer?: Partial<PlayerData>): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const profileResponse = await socialApi.getPlayerAccountProfile(token);
      if (!profileResponse.data?.success || !profileResponse.data?.profile) {
        throw new Error(profileResponse.data?.error || 'Failed to load player profile');
      }

      const p = profileResponse.data.profile as any;
      const uscfId = p.uscfId || fallbackPlayer?.uscfId || '';

      let uscfLookup: any = null;
      if (uscfId) {
        try {
          const lookupResponse = await playerProfileApi.getByUscfId(uscfId);
          if (lookupResponse.data?.success && lookupResponse.data?.player) {
            uscfLookup = lookupResponse.data.player;
          }
        } catch (lookupErr) {
          console.warn('USCF profile lookup failed:', lookupErr);
        }
      }

      const playerData: PlayerData = {
        userId: p.userId,
        username: p.username,
        name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || uscfLookup?.name || p.username || 'Player',
        uscfId: uscfId || undefined,
        fideId: p.fideId || undefined,
        email: p.email || undefined,
        phone: p.phone || undefined,
        rating: uscfLookup?.rating,
        state: uscfLookup?.state,
        // Prefer linked accounts from player_profiles; fall back to USCF lookup
        chesscomUsername: p.chesscomUsername || uscfLookup?.chesscomUsername || undefined,
        lichessUsername: p.lichessUsername || uscfLookup?.lichessUsername || undefined,
        hideLichessPublic: Boolean(p.hideLichessPublic),
        hideChesscomPublic: Boolean(p.hideChesscomPublic),
        avatarUrl: p.avatarUrl || undefined,
        profileComplete: Boolean(p.profileComplete),
        pairingUsername: p.pairingUsername || undefined,
      };

      setAuthToken(token);
      setPlayer(playerData);
      persistAuth(token, playerData);
      await fetchRegisteredTournaments(playerData.uscfId, token);

      if (playerData.uscfId) {
        try {
          await socialApi.syncUscfData(playerData.uscfId);
        } catch (syncErr) {
          console.warn('Failed to sync USCF data:', syncErr);
        }
      }

      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Player auth hydration error:', err);
      setError('Unable to load player account. Please sign in again.');
      setPlayer(null);
      setAuthToken(null);
      setRegisteredTournaments([]);
      localStorage.removeItem(PLAYER_STORAGE_KEY);
      setIsLoading(false);
      return false;
    }
  };

  const loginWithToken = async (token: string, user: any): Promise<boolean> => {
    const fallbackPlayer: Partial<PlayerData> = {
      userId: user?.id,
      username: user?.username,
      name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || 'Player',
      email: user?.email
    };
    return hydrateFromToken(token, fallbackPlayer);
  };

  const accountLogin = async (identifier: string, password: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login({ username: identifier.trim(), password });
      const token = response.data?.data?.token;
      const user = response.data?.data?.user;

      if (!token || !user) {
        setError(response.data?.error || 'Invalid login response.');
        setIsLoading(false);
        return false;
      }

      return await loginWithToken(token, user);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid credentials.');
      setIsLoading(false);
      return false;
    }
  };

  const accountSignup = async (payload: { username: string; email: string; password: string }): Promise<boolean> => {
    setError(null);
    setIsLoading(true);

    try {
      // On a club /org/:slug shadow site the hostname is the platform's, so the
      // club travels in the payload — it is what puts the new account on that
      // club's roster. A tenant hostname is resolved server-side.
      const orgSlug = parseShadowOrgSlug(window.location.pathname);
      const response = await socialApi.signupPlayerAccount(
        orgSlug ? { ...payload, org: orgSlug } : payload
      );
      if (!response.data?.success || !response.data?.data?.token || !response.data?.data?.user) {
        setError(response.data?.error || 'Unable to create account.');
        setIsLoading(false);
        return false;
      }

      return await loginWithToken(response.data.data.token, response.data.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to create account.');
      setIsLoading(false);
      return false;
    }
  };

  const completeProfile = async (payload: { uscfId?: string; noUscfId?: boolean; phone?: string; firstName?: string; lastName?: string; fideId?: string }): Promise<boolean> => {
    if (!authToken) {
      setError('You are not authenticated. Please sign in again.');
      return false;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await socialApi.updatePlayerAccountProfile(authToken, payload);
      if (!response.data?.success || !response.data?.profile) {
        setError(response.data?.error || 'Unable to update profile.');
        setIsLoading(false);
        return false;
      }

      const p = response.data.profile;
      const existing = player;
      const updated: PlayerData = {
        userId: p.userId,
        username: p.username,
        name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || existing?.name || p.username,
        uscfId: p.uscfId || undefined,
        fideId: (p as any).fideId || existing?.fideId,
        email: p.email || existing?.email,
        phone: p.phone,
        rating: existing?.rating,
        state: existing?.state,
        chesscomUsername: existing?.chesscomUsername,
        lichessUsername: existing?.lichessUsername,
        socialProfileId: existing?.socialProfileId,
        profileComplete: Boolean(p.profileComplete),
        pairingUsername: p.pairingUsername || undefined,
      };

      setPlayer(updated);
      persistAuth(authToken, updated);
      await fetchRegisteredTournaments(updated.uscfId, authToken);

      if (updated.uscfId) {
        try {
          await socialApi.syncUscfData(updated.uscfId);
        } catch (syncErr) {
          console.warn('Failed to sync USCF data:', syncErr);
        }
      }

      setIsLoading(false);
      return true;
    } catch (err: any) {
      console.error('Complete profile error:', err);
      setError(err?.response?.data?.error || 'Unable to update profile.');
      setIsLoading(false);
      return false;
    }
  };

  const updateLinkedAccounts = async (data: { lichessUsername?: string; chesscomUsername?: string }): Promise<boolean> => {
    if (!player?.uscfId) return false;
    try {
      await playerProfileApi.updateAccounts(player.uscfId, data);
      updatePlayerInfo(data);
      return true;
    } catch (err) {
      console.error('Failed to update linked accounts:', err);
      return false;
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!authToken) return { success: false, error: 'Not authenticated.' };
    try {
      const response = await socialApi.changePlayerPassword(authToken, oldPassword, newPassword);
      if (!response.data?.success) {
        return { success: false, error: response.data?.error || 'Failed to change password.' };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.response?.data?.error || 'Failed to change password.' };
    }
  };

  const changeEmail = async (newEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!authToken) return { success: false, error: 'Not authenticated.' };
    try {
      const response = await socialApi.changePlayerEmail(authToken, newEmail);
      if (!response.data?.success) {
        return { success: false, error: response.data?.error || 'Failed to change email.' };
      }
      if (response.data.email) {
        updatePlayerInfo({ email: response.data.email });
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.response?.data?.error || 'Failed to change email.' };
    }
  };

  const resolveApiBase = () =>
    process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

  const startGoogleLogin = () => {
    // Pass the organization when signing in from a club /org/:slug shadow site
    // so the server can apply that org's sign-in policy. On a tenant hostname
    // the server resolves the org from the request host itself.
    const orgSlug = parseShadowOrgSlug(window.location.pathname);
    const orgParam = orgSlug ? `&org=${encodeURIComponent(orgSlug)}` : '';
    window.location.href = `${resolveApiBase()}/auth/google?mode=player${orgParam}`;
  };

  const startLichessLogin = () => {
    window.location.href = `${resolveApiBase()}/auth/lichess?mode=player`;
  };

  const startChesscomLogin = async () => {
    setError(null);
    try {
      const response = await fetch(`${resolveApiBase()}/chesscom/oauth/start?source=player-auth`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (data?.success && data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data?.error || 'Unable to start Chess.com sign-in.');
      }
    } catch (err) {
      console.error('Chess.com login start failed:', err);
      setError('Unable to start Chess.com sign-in. Please try again.');
    }
  };

  const logout = () => {
    setPlayer(null);
    setAuthToken(null);
    setRegisteredTournaments([]);
    localStorage.removeItem(PLAYER_STORAGE_KEY);
  };

  const saveUscfId = async (uscfId: string): Promise<{ success: boolean; error?: string }> => {
    const id = String(uscfId || '').trim();
    if (!id) return { success: false, error: 'No US Chess ID to save.' };
    if (!authToken || !player) return { success: false, error: 'Sign in again to save this to your account.' };
    try {
      const response = await socialApi.updatePlayerAccountProfile(authToken, { uscfId: id });
      if (!response.data?.success || !response.data?.profile) {
        return { success: false, error: response.data?.error || 'Could not save it to your account.' };
      }
      const updated: PlayerData = {
        ...player,
        uscfId: response.data.profile.uscfId || id,
        profileComplete: true,
      };
      setPlayer(updated);
      persistAuth(authToken, updated);
      // Pull the federation's record in behind it; the save already stands.
      socialApi.syncUscfData(updated.uscfId as string).catch(() => {});
      return { success: true };
    } catch (err: any) {
      return {
        success: false,
        error: err?.response?.data?.error || 'Could not save it to your account.',
      };
    }
  };

  const updatePlayerInfo = (data: Partial<PlayerData>) => {
    if (player) {
      const updated = { ...player, ...data };
      setPlayer(updated);
      if (authToken) {
        persistAuth(authToken, updated);
      }
    }
  };

  const refreshTournaments = async () => {
    await fetchRegisteredTournaments(player?.uscfId, authToken);
  };

  const needsProfileCompletion = useMemo(() => {
    // First-login onboarding now hinges on the player completing the US Chess ID step
    // (either entering an ID or explicitly opting out), not on having a phone number.
    return Boolean(player && !player.profileComplete);
  }, [player]);

  return (
    <PlayerAuthContext.Provider
      value={{
        player,
        authToken,
        isLoggedIn: !!player,
        isLoading,
        error,
        registeredTournaments,
        needsProfileCompletion,
        accountLogin,
        accountSignup,
        loginWithToken,
        completeProfile,
        startGoogleLogin,
        startLichessLogin,
        startChesscomLogin,
        logout,
        updatePlayerInfo,
        saveUscfId,
        updateLinkedAccounts,
        changePassword,
        changeEmail,
        refreshTournaments,
      }}
    >
      {children}
    </PlayerAuthContext.Provider>
  );
};

export const usePlayerAuth = () => {
  const context = useContext(PlayerAuthContext);
  if (context === undefined) {
    throw new Error('usePlayerAuth must be used within a PlayerAuthProvider');
  }
  return context;
};

export default PlayerAuthContext;
